import { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { DndTypes } from '@work4all/utils/lib/variables';

import { CreditNote } from '../../../components/convert-popover/ConvertableTypes';

import {
  DropTarget,
  useWidgetDrapDropContext,
} from './use-widget-drag-drop-context';

export const useWidgetDrop = (isCustomerToSupplierConversion?: boolean) => {
  const {
    canDrop: canDropItem,
    canSkip,
    onDrop,
    isEntitiesIdentical,
    customerToSupplierConversionEntity,
  } = useWidgetDrapDropContext();

  const dropTargetType: DropTarget | undefined =
    isCustomerToSupplierConversion &&
    (customerToSupplierConversionEntity === Entities.order ||
      customerToSupplierConversionEntity === CreditNote)
      ? customerToSupplierConversionEntity
      : undefined;

  const isCorrect = canDropItem(dropTargetType);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DndTypes.FILE_CARD_WIDGET,
      drop: (item) => {
        if (isCorrect) onDrop(item, dropTargetType);
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
      canDrop: () => canDropItem(dropTargetType),
    }),
    [isCorrect, onDrop, dropTargetType]
  );

  return {
    isOver,
    canDrop: canDrop && !canSkip(),
    drop,
    isCorrect,
    isEntitiesIdentical,
    customerToSupplierConversionEntity,
  };
};

export function useWidgetDrag<T>(item: T) {
  const { canDrag: canDragItem } = useWidgetDrapDropContext();

  const [{ isDragging }, drag, preview] = useDrag(
    {
      type: DndTypes.FILE_CARD_WIDGET,
      item,
      collect(monitor) {
        return {
          isDragging: monitor.isDragging(),
        };
      },
    },
    [item]
  );

  useEffect(() => {
    preview(getEmptyImage());
  }, [preview]);

  return canDragItem()
    ? { isDragging, drag, preview }
    : {
        isDragging: false,
        drag: undefined,
        preview: undefined,
      };
}
